import type { ReactNode } from "react";

// ─── Advanced Filter Types ───────────────────────────────────────────────────

export interface AdvancedFilterItem {
  id: string;
  field: string;
  operator: string;
  value: any;
}

export interface AdvancedFilterGroup {
  id: string;
  logicOperator: "and" | "or";
  items: AdvancedFilterItem[];
}

export interface AdvancedFilterModel {
  groups: AdvancedFilterGroup[];
  groupLogicOperator: "and" | "or";
}

export function countAdvancedFilterItems(
  model: AdvancedFilterModel | null
): number {
  if (!model) return 0;
  return model.groups.reduce((sum, g) => sum + g.items.length, 0);
}

export function hasActiveAdvancedFilters(
  model: AdvancedFilterModel | null
): boolean {
  if (!model) return false;
  return model.groups.some((g) =>
    g.items.some((item) => item.field && item.operator)
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export interface CreateMenuItem {
  label: string;
  icon?: ReactNode;
  onClick: () => void;
  requiresRowSelection?: boolean;
  disabledReason?: string;
}

// ─────────────────────────────────────────────────────────────────────────────

export interface SelectOption {
  value: any;
  label: string;
}

export interface GridResponse {
  data: any[];
  totalCount: number;
  meta?: {
    page: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
  groupedMeta?: {
    page: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
    groupDepth: number;
  };
  options?: Record<string, SelectOption[]>;
}

export interface CRUDResponse {
  success: boolean;
  data?: any;
  errors?: string[];
}

// Bulk update types for batch string operations
export type BulkStringOperation =
  | "append"
  | "prepend"
  | "remove"
  | "replace"
  | "clear";

export interface BulkStringOperationConfig {
  operation: BulkStringOperation;
  value: string;
  findValue?: string; // Required for 'replace' operation
  isRegex?: boolean; // Use REGEXP_REPLACE instead of REPLACE
}

export interface BulkUpdateRequest {
  ids: (string | number)[];
  // ── String-operations mode (existing) ──────────────────────────
  field?: string;
  operations?: BulkStringOperationConfig[];
  // ── Multi-field direct-set mode (new) ──────────────────────────
  fields?: Record<string, any>; // { fieldName: newValue, ... }
}

export interface BulkUpdateResponse {
  success: boolean;
  affectedRows: number;
  errors?: string[];
  data?: {
    updatedIds: (string | number)[];
    failedIds?: (string | number)[];
  };
}
